import React from "react";

/**
 * Tính mảng số trang hiển thị kiểu:
 *   [1, '…', 4, 5, 6, '…', 20]
 *
 * @param {number} current
 * @param {number} total
 * @param {number} siblings - số trang kề cạnh current mỗi bên (default 1)
 * @returns {Array<number|'…'>}
 */
function getPageNumbers(current, total, siblings = 1) {
    if (total <= 0) return [];

    const totalNumbers = siblings * 2 + 5; // first + last + current + 2*siblings + 2 dots

    if (total <= totalNumbers) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }

    const leftSibling = Math.max(current - siblings, 1);
    const rightSibling = Math.min(current + siblings, total);

    const showLeftDots = leftSibling > 2;
    const showRightDots = rightSibling < total - 1;

    const pages = [];

    if (!showLeftDots && showRightDots) {
        const leftRange = Array.from(
            { length: 3 + siblings * 2 },
            (_, i) => i + 1,
        );
        pages.push(...leftRange, "…", total);
    } else if (showLeftDots && !showRightDots) {
        const rightStart = total - (3 + siblings * 2) + 1;
        const rightRange = Array.from(
            { length: 3 + siblings * 2 },
            (_, i) => rightStart + i,
        );
        pages.push(1, "…", ...rightRange);
    } else if (showLeftDots && showRightDots) {
        const middleRange = Array.from(
            { length: rightSibling - leftSibling + 1 },
            (_, i) => leftSibling + i,
        );
        pages.push(1, "…", ...middleRange, "…", total);
    }

    return pages;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * @param {Object} props
 * @param {number} props.page           - current page (1-based)
 * @param {number} props.totalPages
 * @param {number} props.totalItems     - tổng số bản ghi sau filter
 * @param {number} props.pageSize
 * @param {boolean} [props.loading]
 * @param {(p:number)=>void} props.onPageChange
 * @param {(s:number)=>void} [props.onPageSizeChange]
 * @param {string} [props.itemLabel]    - ví dụ "user"
 */
function Pagination({
    page,
    totalPages,
    totalItems,
    pageSize,
    loading = false,
    onPageChange,
    onPageSizeChange,
    itemLabel = "mục",
}) {
    if (totalPages <= 0) return null;

    const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, totalItems);

    const pages = getPageNumbers(page, totalPages, 1);

    const goTo = (p) => {
        if (loading) return;
        const clamped = Math.max(1, Math.min(totalPages, p));
        if (clamped !== page) onPageChange(clamped);
    };

    const BtnNav = ({ disabled, onClick, title, children }) => (
        <button
            type="button"
            disabled={disabled || loading}
            onClick={onClick}
            title={title}
            className="inline-flex items-center justify-center w-8 h-8 text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
        >
            {children}
        </button>
    );

    return (
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 bg-gray-50">
            {/* Left: summary + page size */}
            <div className="flex items-center gap-3 flex-wrap">
                <span className="text-xs text-gray-500">
                    Hiển thị{" "}
                    <span className="font-semibold text-gray-700">{from}</span>
                    {" – "}
                    <span className="font-semibold text-gray-700">{to}</span>
                    {" / "}
                    <span className="font-semibold text-gray-700">
                        {totalItems}
                    </span>{" "}
                    {itemLabel}
                </span>

                {onPageSizeChange && (
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-500">Mỗi trang:</span>
                        <select
                            value={pageSize}
                            onChange={(e) =>
                                onPageSizeChange(parseInt(e.target.value, 10))
                            }
                            disabled={loading}
                            className="px-2 py-1 text-xs border border-gray-300 rounded-md bg-white outline-none focus:border-red-600"
                        >
                            {PAGE_SIZE_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                    {opt}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* Right: page buttons */}
            <div className="flex items-center gap-1">
                <BtnNav
                    disabled={page <= 1}
                    onClick={() => goTo(1)}
                    title="Trang đầu"
                >
                    <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
                        />
                    </svg>
                </BtnNav>

                <BtnNav
                    disabled={page <= 1}
                    onClick={() => goTo(page - 1)}
                    title="Trang trước"
                >
                    <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                        />
                    </svg>
                </BtnNav>

                {pages.map((p, i) =>
                    p === "…" ? (
                        <span
                            key={`dots-${i}`}
                            className="px-2 text-xs text-gray-400 select-none"
                        >
                            …
                        </span>
                    ) : (
                        <button
                            key={p}
                            type="button"
                            disabled={loading}
                            onClick={() => goTo(p)}
                            className={`inline-flex items-center justify-center min-w-8 h-8 px-2 text-xs font-medium rounded-md border transition-colors ${p === page
                                    ? "bg-red-600 text-white border-red-600"
                                    : "text-gray-700 bg-white border-gray-300 hover:bg-gray-50"
                                } disabled:opacity-60`}
                        >
                            {p}
                        </button>
                    ),
                )}

                <BtnNav
                    disabled={page >= totalPages}
                    onClick={() => goTo(page + 1)}
                    title="Trang sau"
                >
                    <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                        />
                    </svg>
                </BtnNav>

                <BtnNav
                    disabled={page >= totalPages}
                    onClick={() => goTo(totalPages)}
                    title="Trang cuối"
                >
                    <svg
                        className="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13 5l7 7-7 7M5 5l7 7-7 7"
                        />
                    </svg>
                </BtnNav>
            </div>
        </div>
    );
}

export default Pagination;
